// Analytics Service
import { collection, addDoc, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db, analytics } from '../lib/firebase';
import { logEvent } from 'firebase/analytics';

export const AnalyticsService = {
  // Track page view
  trackPageView(pageName) {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: pageName,
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }
  },

  // Track product view
  trackProductView(product) {
    if (analytics) {
      logEvent(analytics, 'view_item', {
        items: [{
          item_id: product.id,
          item_name: product.name,
          item_category: product.category,
          price: product.price
        }]
      });
    }
  },

  // Track add to cart
  trackAddToCart(product, quantity) {
    if (analytics) {
      logEvent(analytics, 'add_to_cart', {
        items: [{
          item_id: product.id,
          item_name: product.name,
          item_category: product.category,
          price: product.price,
          quantity: quantity
        }],
        value: product.price * quantity,
        currency: 'USD'
      });
    }
  },

  // Track purchase
  trackPurchase(order) {
    if (analytics) {
      logEvent(analytics, 'purchase', {
        transaction_id: order.id,
        value: order.total,
        currency: 'USD',
        items: order.items.map(item => ({
          item_id: item.id,
          item_name: item.name,
          item_category: item.category,
          price: item.price,
          quantity: item.quantity
        }))
      });
    }
  },

  // Track search
  trackSearch(searchTerm) {
    if (analytics) {
      logEvent(analytics, 'search', {
        search_term: searchTerm
      });
    }
  },

  // Log custom event to Firestore for admin dashboard
  async logCustomEvent(eventType, eventData) {
    try {
      await addDoc(collection(db, 'analytics_events'), {
        type: eventType,
        data: eventData,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    } catch (error) {
      console.error('Error logging event:', error);
    }
  },

  // Get analytics data for admin dashboard
  async getAnalyticsData(startDate, endDate) {
    try {
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
      const end = endDate || new Date();

      const q = query(
        collection(db, 'analytics_events'),
        where('timestamp', '>=', start.toISOString()),
        where('timestamp', '<=', end.toISOString()),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Aggregate data
      const analytics = {
        totalEvents: events.length,
        eventsByType: {},
        dailyEvents: {},
        topProducts: {},
        topSearches: {}
      };

      events.forEach(event => {
        // Count by type
        analytics.eventsByType[event.type] = (analytics.eventsByType[event.type] || 0) + 1;

        // Count by day
        const day = event.timestamp.split('T')[0];
        analytics.dailyEvents[day] = (analytics.dailyEvents[day] || 0) + 1;

        // Track product views
        if (event.type === 'product_view' && event.data?.productId) {
          analytics.topProducts[event.data.productId] = (analytics.topProducts[event.data.productId] || 0) + 1;
        }

        // Track searches
        if (event.type === 'search' && event.data?.query) {
          analytics.topSearches[event.data.query] = (analytics.topSearches[event.data.query] || 0) + 1;
        }
      });

      return analytics;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }
};
